export const formatUsd = (value) =>
  Number(value).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const formatMarketPrice = (value) => {
  const num = Number(value);
  const fractionDigits = num >= 1 ? 2 : 4;

  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

export const formatLargeUsd = (value) =>
  `$${Number(value).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  })}`;

export const formatCryptoAmount = (value) => {
  const num = Number(value);
  if (!value || Number.isNaN(num)) return "0.00";
  if (num < 0.0001) return num.toFixed(8);
  if (num < 1) return num.toFixed(4);
  return num.toFixed(2);
};

export const formatFiatAmount = (value) => {
  const num = Number(value);
  const fractionDigits = num >= 1 ? 2 : 4;

  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`;
};

export const formatShortUsd = (value) => {
  const num = Number(value);
  if (num >= 1e12) return `$${(num / 1e12).toFixed(1)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
  return formatLargeUsd(num);
};

export const formatChartAxisLabel = (value) => {
  const num = Number(value);
  if (num >= 1e12) return `$${(num / 1e12).toFixed(1)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(num >= 10e9 ? 0 : 1)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(num >= 10e6 ? 0 : 1)}M`;
  if (num >= 10000) return `$${Math.round(num / 1000)}K`;
  if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`;
  if (num >= 1) return `$${Math.round(num)}`;
  if (num >= 0.01) return `$${num.toFixed(2)}`;
  return `$${num.toFixed(4)}`;
};

export const parseChartAxisLabel = (label) => {
  const cleaned = label.replace(/[$,]/g, "").trim();
  const suffix = cleaned.slice(-1).toUpperCase();
  const num = parseFloat(cleaned);

  if (Number.isNaN(num)) return 0;
  if (suffix === "T") return num * 1e12;
  if (suffix === "B") return num * 1e9;
  if (suffix === "M") return num * 1e6;
  if (suffix === "K") return num * 1e3;
  return num;
};


export const formatFiatDisplay = (value) =>
`$${value.toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`


export const formatSwapAmount = (amount, symbol = "") => {
  const num = Number(amount);
  if (Number.isNaN(num)) return `0 ${symbol}`.trim();

  // If amount has decimals, show up to 8 places (omits trailing zeros).
  // If whole number, shows 0 decimal places.
  const hasDecimals = !Number.isInteger(num);

  const formatted = num.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: hasDecimals ? 9 : 0,
  });

  return symbol ? `${formatted} ${symbol}` : formatted;
};

export const formatFiatLabel = (value) =>
`$${Number(value).toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;




export const getOrderSummary = (orderData, fromAsset, toAsset) => {
  switch (orderData.side) {
    case "buy":
      return {
        cryptoAccount: toAsset,
        paymentAccount: fromAsset,
        cryptoAmount: Number(orderData.filled_amount),
        paymentAmount: Number(orderData.sub_total),
      };

    case "sell":
      return {
        cryptoAccount: fromAsset,
        paymentAccount: toAsset,
        cryptoAmount: Number(orderData.filled_amount),
        paymentAmount: Number(orderData.sub_total),
      };

    case "swap":
      return {
        cryptoAccount: fromAsset,
        paymentAccount: toAsset,
        cryptoAmount: Number(orderData.quantity),
        paymentAmount: Number(orderData.sub_total),
      };

    default:
      return {};
  }
};



/**
 * Formats cryptocurrency values.
 * 
 * Rules:
 * - If `decimals` is provided: truncates to that maximum precision, keeping at least 2 decimals.
 * - If `decimals` is null/undefined:
 *     - Single/whole numbers (e.g. 1) -> pads to 2 decimals ("1.00")
 *     - Existing decimals (e.g. 1.99999) -> keeps the full decimal portion ("1.99999")
 */
export const formatCryptoValue = (value, decimals = null) => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
    return '0.00';
  }

  const [whole, rawFraction = ''] = String(value).split('.');

  // Case 1: No decimals limit provided (null / undefined)
  if (decimals === null || decimals === undefined) {
    if (!rawFraction) {
      return `${whole}.00`;
    }
    // If it has 1 decimal place like "1.5", pad to at least 2 -> "1.50"
    if (rawFraction.length === 1) {
      return `${whole}.${rawFraction}0`;
    }
    // Retain full fraction (e.g. "1.99999")
    return `${whole}.${rawFraction}`;
  }

  // Case 2: decimals is explicitly 0
  if (Number(decimals) === 0) {
    return whole;
  }

  // Case 3: decimals limit is provided
  const maxDecimals = Number(decimals);
  const minDecimals = Math.min(2, maxDecimals);

  let fraction = rawFraction.slice(0, maxDecimals);

  // Pad to reach at least minDecimals (default 2), then trim trailing zeros beyond that
  const baseFraction = fraction.slice(0, minDecimals).padEnd(minDecimals, '0');
  const extraFraction = fraction.slice(minDecimals).replace(/0+$/, '');

  return `${whole}.${baseFraction}${extraFraction}`;
};


/**
 * Formats fiat currency values.
 * 
 * Rules:
 * - Defaults to 2 decimal places (e.g. 1 -> "1.00", 12.3456 -> "12.35").
 * - If `decimals` is provided and greater than 2, it uses that precision.
 */
export const formatFiatValue = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) {
    return '0.00';
  }

  const num = Number(value);
  // Ensure we display at least 2 decimals, or higher if passed
  const precision = Math.max(2, Number(decimals) || 2);

  return num.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
    useGrouping: false // Set to true if you want comma separators like "1,000.00"
  });
};